import React, { useEffect, useState } from "react";
import { Mic, Star, Trash2, Check, X, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";

export default function BlessingsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all"); // all / pending / approved

  async function load() {
    const { data } = await api.get("/blessings/mine");
    setItems(data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function approve(id, is_approved, is_featured) {
    try {
      await api.patch(`/blessings/${id}`, { is_approved, is_featured });
      toast.success(is_approved ? "Approved" : "Reverted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this blessing?")) return;
    await api.delete(`/blessings/${id}`);
    load();
  }

  const filtered = items.filter((b) =>
    filter === "all" ? true : filter === "approved" ? b.is_approved : !b.is_approved
  );

  return (
    <div data-testid="blessings-page">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-1 flex items-center gap-2">
          <Mic className="w-3 h-3" /> Audio Blessings
        </p>
        <h1 className="font-serif text-4xl md:text-5xl">Voices of <span className="font-script text-[var(--gold)]">your day</span></h1>
        <p className="text-sm text-[var(--muted)] mt-2 max-w-xl">
          Guests can record short voice notes from your wedding site. Moderate them here, feature your favorites, and play them back forever.
        </p>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          { v: "all", l: "All", c: items.length },
          { v: "pending", l: "Pending", c: items.filter((b) => !b.is_approved).length },
          { v: "approved", l: "Approved", c: items.filter((b) => b.is_approved).length },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setFilter(t.v)}
            data-testid={`filter-${t.v}`}
            className={`text-xs px-4 py-2 rounded-full border ${filter === t.v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "bg-white border-[var(--line)] text-[var(--muted)] hover:border-[var(--gold)]"}`}
          >
            {t.l} <span className="opacity-60 ml-1">{t.c}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--line)] rounded-2xl">
          <Mic className="w-10 h-10 mx-auto text-[var(--gold)] mb-3" />
          <p className="font-serif text-2xl">No blessings yet</p>
          <p className="text-sm text-[var(--muted)] mt-2">Share your wedding site link with guests and let them leave a voice note from /site/&lt;your-slug&gt;.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="blessings-list">
          {filtered.map((b) => (
            <div key={b.blessing_id} className="bg-white border border-[var(--line)] rounded-2xl p-5 flex items-start gap-4" data-testid={`blessing-${b.blessing_id}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${b.is_approved ? "bg-green-100 text-green-700" : "bg-[var(--bg-warm)] text-[var(--muted)]"}`}>
                {b.is_featured ? <Star className="w-5 h-5 fill-current" /> : <Mic className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <p className="font-medium">{b.guest_name}</p>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
                    {new Date(b.created_at).toLocaleDateString()}
                  </span>
                  {b.is_approved && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">Approved</span>}
                  {b.is_featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Featured</span>}
                </div>
                {b.message && <p className="text-sm text-[var(--muted)] mt-1">{b.message}</p>}
                <audio
                  controls
                  src={`${BACKEND_URL}/api/files/${b.audio_path}`}
                  className="w-full mt-3"
                  data-testid={`audio-${b.blessing_id}`}
                />
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                {!b.is_approved ? (
                  <button onClick={() => approve(b.blessing_id, true, false)} title="Approve" data-testid={`approve-${b.blessing_id}`} className="p-2 text-green-600 hover:bg-green-50 rounded-lg"><Check className="w-4 h-4" /></button>
                ) : (
                  <>
                    <button
                      onClick={() => approve(b.blessing_id, true, !b.is_featured)}
                      title={b.is_featured ? "Unfeature" : "Feature"}
                      data-testid={`feature-${b.blessing_id}`}
                      className={`p-2 rounded-lg ${b.is_featured ? "text-amber-600 bg-amber-50" : "text-[var(--muted)] hover:text-amber-600 hover:bg-amber-50"}`}
                    >
                      <Star className="w-4 h-4" fill={b.is_featured ? "currentColor" : "none"} />
                    </button>
                    <button onClick={() => approve(b.blessing_id, false, false)} title="Unapprove" data-testid={`unapprove-${b.blessing_id}`} className="p-2 text-[var(--muted)] hover:text-amber-600 rounded-lg"><X className="w-4 h-4" /></button>
                  </>
                )}
                <button onClick={() => remove(b.blessing_id)} title="Delete" data-testid={`delete-${b.blessing_id}`} className="p-2 text-[var(--muted)] hover:text-red-600 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
